import { EmployeeRegistryClient } from "@/Common/Api/Employee/EmployeeRegistryClient";
import { ExternalEmployeeResponse } from "@/Common/Api/ExternalCommunications/Dto/Responses/ExternalEmployeeResponse";
import { FaxIcon } from "@/Common/Components/IconControls/FaxIcon";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";

import styles from "./SearchClinicDialog.module.scss";

interface ISearchClinicDialogProps {
    onClose: () => void;
    onSelected: (employee: ExternalEmployeeResponse) => void;
    visible: boolean;
}

function SearchClinicDialog(props: ISearchClinicDialogProps): JSX.Element {
    const [searchText, setSearchText] = React.useState("");
    const [employees, setEmployees] = React.useState<ExternalEmployeeResponse[]>([]);
    const [hasReceivedSearchResults, setHasReceivedSearchResults] = React.useState(false);

    const employeeRegClient = React.useMemo(() => EmployeeRegistryClient.getInstanceV2(), []);
    const footer = React.useMemo(() => (
        <div className="display-flex-justified-end">
            <div className="margin-xs-left">
                <button className="btn btn-default" onClick={() => props.onClose()}>
                    {cacheStore.GetString("CancelLabel")}
                </button>
            </div>
        </div>
    ), [props]);

    const employeeRows = React.useMemo(() => employees.map(e => renderEmployeeRow(e, props.onSelected)), [employees, props]);

    React.useEffect(() => {
        if (!searchText || !searchText.length || !employeeRegClient) {
            setEmployees([]);
            return;
        }

        employeeRegClient.get(
            `Search/${searchText}`,
            null,
            employees => {
                setHasReceivedSearchResults(true);
                if (!employees || !Array.isArray(employees))
                    setEmployees([]);
                else
                    setEmployees(employees.map(ExternalEmployeeResponse.fromEmployeeRegResponse));
            }
        );
    }, [searchText, employeeRegClient]);

    return props.visible ? (
        <Modal
            showOverlay
            fixedOverlayPosition
            showX
            centerContent
            title={cacheStore.GetString("SearchClinicTitle")}
            onClose={props.onClose}
            footer={footer}
            disableOverlayClose
        >
            <p>{cacheStore.GetString("SearchClinicDescription")}</p>

            <TextboxControl
                debounceMs={400}
                value={searchText}
                onChange={setSearchText}
                className={`form-control ${styles.input}`}
                containerCssClass={styles.textbox}
                placeholder={cacheStore.GetString("SearchLabel")}
            />

            {hasReceivedSearchResults && employeeRows.length === 0 && !!searchText.length && <p>{cacheStore.GetString("SearchClinicNoClinicFound")}</p>}
            {!!employeeRows.length && (
                <div className={`scrollable ${styles.results}`}>
                    {employeeRows}
                </div>
            )}
        </Modal>
    ) : null;
}

function renderEmployeeRow(employee: ExternalEmployeeResponse, onSelected: (employee: ExternalEmployeeResponse) => void): JSX.Element {
    const fax = employee.SitePhones?.filter(p => p.PhoneCode === "FAX")
        .firstPreferredOrDefault(p => p.IsPrefered);
    const phone = employee.SitePhones?.filter(p => p.PhoneCode === "PHONE")
        .firstPreferredOrDefault(p => p.IsPrefered);

    return (
        <div
            key={`${employee.EmployeeId}-${employee.OrganizationId}-${employee.ClientTag}`}
            className={`pointer ${styles.row}`}
            onClick={() => onSelected(employee)}
        >
            <div className={styles.col}>
                <div className={styles.element}>
                    <strong className={styles.title}>{cacheStore.GetString("OrganizationLabel")}</strong>
                    <p className={styles.value}>{employee.OrganizationName}</p>
                </div>
                <div className={styles.element}>
                    <strong className={styles.title}>{cacheStore.GetString("SiteDLabel")}</strong>
                    <p className={styles.value}>{employee.SiteName}</p>
                </div>
                <div className={styles.element}>
                    <strong className={styles.name}>{employee.getNameWithPrefix()} {!!employee.LicenseNumber ? `(${employee.LicenseNumber})` : ""}</strong>
                </div>
            </div>
            <div className={styles.col}>
                {!!phone?.Number && (
                    <div className={styles.element}>
                        <i className={`fi-phone ${styles.icon}`} />
                        <p>{MyleHelper.FormatPhone(phone.Number, phone.CountryCode)}</p>
                    </div>
                )}
                {!!fax?.Number && (
                    <div className={styles.element}>
                        <FaxIcon className={`${styles.faxIcon} ${styles.icon}`} />
                        <p>{MyleHelper.FormatPhone(fax.Number, fax.CountryCode)}</p>
                    </div>
                )}
                {!!employee.SiteAddress && (
                    <div className={styles.element}>
                        <i className={`fi-location ${styles.icon}`} />
                        <p>{employee.getFormattedAddress()}</p>
                    </div>
                )}
            </div>
        </div>
    );
}

export { SearchClinicDialog };
